import { ConfirmDialogData } from '@models';
import { OrderItemTrackingStatusEnum, OrderLineItemDTO, OrderStatusEnum } from './order.model';

// Used for checking if the order item status can be changed to another status
// As key uses the current status and as value the list of statuses that can be changed to
export const ORDER_ITEM_CHANGE_STATUS_WHITE_LIST: Record<OrderItemTrackingStatusEnum, OrderItemTrackingStatusEnum[]> = {
  [OrderItemTrackingStatusEnum.Submitted]: [
    OrderItemTrackingStatusEnum.Canceled,
    OrderItemTrackingStatusEnum.FraudHeld,
    OrderItemTrackingStatusEnum.Ordered,
  ],
  [OrderItemTrackingStatusEnum.Ordered]: [
    OrderItemTrackingStatusEnum.PaymentPending,
    OrderItemTrackingStatusEnum.Canceled,
    OrderItemTrackingStatusEnum.Shipped,
  ],
  [OrderItemTrackingStatusEnum.PaymentPending]: [
    OrderItemTrackingStatusEnum.Submitted,
    OrderItemTrackingStatusEnum.PaymentFailed,
  ],
  [OrderItemTrackingStatusEnum.Shipped]: [OrderItemTrackingStatusEnum.Delivered],
  [OrderItemTrackingStatusEnum.Delivered]: [OrderItemTrackingStatusEnum.ReturnStarted],
  [OrderItemTrackingStatusEnum.Canceled]: [],
  [OrderItemTrackingStatusEnum.ReturnStarted]: [OrderItemTrackingStatusEnum.ReturnReceived],
  [OrderItemTrackingStatusEnum.PaymentFailed]: [],
  [OrderItemTrackingStatusEnum.FraudHeld]: [],
  [OrderItemTrackingStatusEnum.ReturnReceived]: [],
  [OrderItemTrackingStatusEnum.Failed]: [],
  [OrderItemTrackingStatusEnum.DeliveredInReturn]: [],
  [OrderItemTrackingStatusEnum.Returned]: [],
  [OrderItemTrackingStatusEnum.Declined]: [],
};
// Used for checking if the order item status need confirmation dialog before changing to another status
// As key uses the current status and as value the confirmation dialog data
export const ORDER_ITEM_CHANGE_STATUS_MODAL_PROCESSING: Partial<
  Record<OrderItemTrackingStatusEnum, ConfirmDialogData>
> = {
  [OrderItemTrackingStatusEnum.Submitted]: {
    title: 'Confirm item processing',
    message:
      'Are you sure you would like to process this order? It can be under the fraud check for 16-30 minutes after it was placed, so it can not be processed yet.',
    confirmMessage: 'Yes',
    cancelMessage: 'No',
  },
};

// One type for all check functions, to unify the checking
export type CheckLineItemStatus = <T extends OrderLineItemDTO>(
  currentStatus: OrderItemTrackingStatusEnum,
  newStatus: OrderItemTrackingStatusEnum,
  allLineItems?: T[],
) => boolean;

// Used for checking all items status.
// As key uses the new status and as value the list of statuses that should be checked
export const WHITE_LIST_FOR_LINE_ITEM_STATUS_EXTRA_CHECK_ALL_ITEMS: Partial<
  Record<OrderItemTrackingStatusEnum, OrderItemTrackingStatusEnum[]>
> = {
  [OrderItemTrackingStatusEnum.ReturnStarted]: [
    OrderItemTrackingStatusEnum.Shipped,
    OrderItemTrackingStatusEnum.Delivered,
    OrderItemTrackingStatusEnum.Canceled,
    OrderItemTrackingStatusEnum.ReturnStarted,
    OrderItemTrackingStatusEnum.ReturnReceived,
  ],
};

export const checkAllItemsStatus: CheckLineItemStatus = (currentStatus, newStatus, items): boolean => {
  const whiteList = WHITE_LIST_FOR_LINE_ITEM_STATUS_EXTRA_CHECK_ALL_ITEMS[newStatus];
  if (whiteList?.length && items?.length) {
    return items.every((item) => whiteList.includes(item.status));
  }
  return false;
};

// Only some statuses require extra check
export const EXTRA_CHECK_FOR_LINE_ITEM_STATUS: Partial<Record<OrderItemTrackingStatusEnum, CheckLineItemStatus>> = {
  [OrderItemTrackingStatusEnum.ReturnStarted]: checkAllItemsStatus,
};
// Main function for check lineItem status change
export const isOrderItemStatusChangeAllowed: CheckLineItemStatus = (
  currentStatus,
  newStatus,
  allLineItems = [],
): boolean => {
  const extraCheck = EXTRA_CHECK_FOR_LINE_ITEM_STATUS[newStatus];
  const passed = extraCheck ? extraCheck(currentStatus, newStatus, allLineItems) : true;
  return ORDER_ITEM_CHANGE_STATUS_WHITE_LIST[currentStatus].includes(newStatus) && passed;
};

// For checking if the ORDER status can be changed to another status
// For now only one status is available for change and could be accepted or rejected only. All other changes for order statuses will be on BE.
// When admin changes order status by accepting or rejecting, the order's line items statuses will be changed by BE.
export const isOrderStatusAvailableForFraudChange = (status: OrderStatusEnum): boolean =>
  OrderStatusEnum.FraudHeld === status;
